import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  bool, number, oneOf, oneOfType, shape, string
} from 'prop-types';
import { Image } from '@thd-olt-component-react/core-ui';
import classNames from 'classnames';
import { VideoPlayerContext } from '../contexts/VideoPlayerContext';
import styles from './video-player.module.scss';
import {
  destroyYouTubePlayer,
  initializeYouTubePlayers,
  initializeBrightCovePlayer,
  youTubePlayers,
  setVolume,
  toggleMuteOnVideo
} from '../helpers/videoPlayerHelpers';
import {
  BRIGHTCOVE_ACCOUNT_ID,
  BRIGHTCOVE_PROD_PLAYER_ID,
  BRIGHTCOVE_TEST_PLAYER_ID
} from '../constants';

export const VideoPlayer = ({
  analyticsData,
  aspectRatio,
  autoPlay,
  className,
  height,
  videoId,
  videoSrc,
  videoStill,
  width,
  source
}) => {
  const iFrameWrapper = useRef();
  const { autoPlayInViewport, playObserver, pauseObserver } = useContext(VideoPlayerContext) || {};

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('video-player.ready');
  });
  const [enableVideoStill, setEnableVideoStill] = useState(!!videoStill);

  const [iframeID, setIframeID] = useState(null);

  useEffect(() => {
    setIframeID(videoSrc
      ? `thd-youtube-iframe-${videoSrc}_${Date.now()}`
      : `thd-brightcove-iframe-${videoId}_${Date.now()}`);
  }, []);

  useEffect(() => {
    if (autoPlayInViewport && !!iFrameWrapper.current && typeof window !== 'undefined') {
      playObserver.observe(iFrameWrapper.current);
      pauseObserver.observe(iFrameWrapper.current);
    }

    return () => {
      if (autoPlayInViewport && !!iFrameWrapper.current && typeof window !== 'undefined') {
        playObserver.unobserve(iFrameWrapper.current);
        pauseObserver.unobserve(iFrameWrapper.current);
      }
    };
  }, [iFrameWrapper.current]);

  useEffect(() => {
    const handleEqualizeVolume = ({ output }) => {
      if (output.id !== iframeID) {
        setVolume({ frame: iFrameWrapper.current, volume: output.volume });
        toggleMuteOnVideo({ frame: iFrameWrapper.current, mute: output.mute });
      }
    };

    if (!enableVideoStill && !!iframeID) {
      if (videoSrc && !youTubePlayers[iframeID]) {
        initializeYouTubePlayers({ analyticsData, muted: autoPlayInViewport, id: iframeID });
      } else if (videoId) {
        initializeBrightCovePlayer({ analyticsData, muted: autoPlay || autoPlayInViewport || videoStill });
      }
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('video-player.equalize-volume', handleEqualizeVolume);
    }

    return () => {
      if (videoSrc) {
        destroyYouTubePlayer({ id: iframeID });
      }
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off('video-player.equalize-volume', handleEqualizeVolume);
    };
  }, [enableVideoStill, iframeID]);

  const containerStyle = {
    width,
    maxWidth: width,
  };

  if (enableVideoStill) {
    const videoStillWrapperStyle = classNames(
      styles['video-player__wrapper'],
      styles['video-player__wrapper--still']
    );
    const playButtonStyles = classNames(styles['video-player__play-button']);
    return (
      <div className={styles['video-player__container']} style={containerStyle}>
        <div className={videoStillWrapperStyle}>
          <Image
            alt="thumbnail"
            className={styles['video-player__still']}
            height={height}
            src={videoStill}
            width={width}
          />
          <button
            alt="Play Button"
            aria-label="play video"
            className={playButtonStyles}
            onClick={() => setEnableVideoStill(false)}
            type="button"
          />
        </div>
      </div>
    );
  }

  if (videoSrc) {
    const videoSource = source === 'CONFIGURABLE_S3'
      ? videoSrc
      : `${videoSrc}?autoplay=${!!(autoPlay || videoStill)}&rel=0&enablejsapi=1`;

    if (source === 'CONFIGURABLE_S3') {
      return (
        <div className={styles['video-player__container']} style={containerStyle}>
          <video
            controls
            muted
            ref={iFrameWrapper}
            id={iframeID}
            className={className}
            title={videoSrc}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        </div>
      );
    }

    return (
      <div className={styles['video-player__container']} style={containerStyle}>
        <iframe
          id={iframeID}
          ref={iFrameWrapper}
          className={className}
          src={videoSource}
          title={videoSrc}
          allowFullScreen
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          height={height}
          width={width}
        />
      </div>
    );
  }

  // eslint-disable-next-line max-len
  let iframeSrc = `https://players.brightcove.net/${BRIGHTCOVE_ACCOUNT_ID}/${BRIGHTCOVE_PROD_PLAYER_ID}_default/index.html?videoId=${videoId}&uniqueId=${iframeID}`;
  if (autoPlay || autoPlayInViewport || videoStill) {
    iframeSrc += '&playsinline=true';
    if (autoPlay || videoStill) {
      iframeSrc += '&autoplay=any';
    } else { // autoPlayInViewport === true
      iframeSrc += '&muted=true';
    }
  }

  const videoWrapperStyle = classNames(
    styles['video-player__wrapper'],
    styles['video-player__responsive'],
    {
      [styles['video-player__responsive--16-9']]: aspectRatio === 16 / 9,
      [styles['video-player__responsive--4-3']]: aspectRatio === 4 / 3,
    }
  );

  return (
    <div className={styles['video-player__container']} style={containerStyle}>
      <div className={videoWrapperStyle}>
        <iframe
          id={iframeID}
          ref={iFrameWrapper}
          className={classNames(styles['video-player'], className)}
          height={height}
          src={iframeSrc}
          title={videoId}
          allow="autoplay"
          allowFullScreen
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          width={width}
        />
      </div>
    </div>
  );
};

VideoPlayer.propTypes = {
  analyticsData: shape({}),
  aspectRatio: oneOf([16 / 9, 4 / 3]),
  autoPlay: bool,
  className: string,
  height: number,
  source: string,
  videoId: oneOfType([string, number]),
  videoSrc: string,
  videoStill: string,
  width: number
};

VideoPlayer.defaultProps = {
  analyticsData: null,
  aspectRatio: 16 / 9,
  autoPlay: false,
  className: null,
  height: null,
  source: null,
  videoId: null,
  videoSrc: null,
  videoStill: null,
  width: null
};